import { type ReactNode } from 'react';

import { TabOrientation } from '@/lib/types';
import { blue, slate } from '@/lib/colours';
import { isNullOrUndefined } from '@/lib/comparison';
import { EventType } from '@/lib/types';

import { Box, Chip, Typography } from '@/primitives';
import { ChipOwnProps } from '@mui/material';

export interface TabDescriptor<T> {
  badgeContent?: ReactNode;
  label: ReactNode;
  value: T;
  disabled?: boolean;
}

export interface TabsProps<T> {
  onClick?: (value: T) => void;
  orientation?: TabOrientation;
  tabs: Array<TabDescriptor<T>>;
  value: string;
  trackTabChange?: (event: any) => void;
  trackEventName?: string;
  eventType?: EventType;
  tabButtonProps?: ChipOwnProps;
}

export function Tabs<T>({
  onClick,
  orientation,
  tabs,
  value,
  trackTabChange,
  trackEventName,
  tabButtonProps,
  eventType = EventType.TAB_SWITCH,
}: TabsProps<T>) {
  const handleClick = (tabValue: T) => {
    if (trackTabChange && trackEventName) {
      trackTabChange({
        name: trackEventName,
        properties: {
          eventType,
          selectedTab: tabValue,
        },
      });
    }
    if (onClick) {
      onClick(tabValue);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: orientation,
        gap: '8px',
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.value === value;
        return (
          <Chip
            {...tabButtonProps}
            disabled={tab.disabled}
            aria-current={isSelected}
            key={index}
            className={isSelected ? 'selectedTab' : ''}
            label={
              <>
                <Typography variant="labellarge">{tab.label}</Typography>
                {!isNullOrUndefined(tab.badgeContent) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: slate[100],
                      borderRadius: '28px',
                      height: '18px',
                      minWidth: '18px',
                      padding: '2px',
                    }}
                  >
                    <Typography variant="labelsmall">
                      {tab.badgeContent}
                    </Typography>
                  </Box>
                )}
              </>
            }
            onClick={() => handleClick(tab.value)}
            sx={{
              '& .MuiChip-label': {
                display: 'flex',
                gap: '8px',
                padding: '0',
              },
              backgroundColor: isSelected ? blue[100] : 'inherit',
              color: isSelected ? blue[600] : 'inherit',
              padding: '24px',
              ...(orientation === TabOrientation.Vertical && {
                justifyContent: 'flex-start',
                width: '100%',
              }),
              ...tabButtonProps?.sx,
            }}
          />
        );
      })}
    </Box>
  );
}
